import { supabase } from '@/lib/safeSupabase';
import { normalizeAltFundFields } from './altFundFields';
import { ensureUser, withAuthRetry } from '@/lib/authResilience';
import type { TaxScenarioInput, TaxScenario } from './types';
import { calculateTaxServer } from './taxApi';

/** Get current user's org_id from their profile */
async function getUserOrgId(): Promise<string | null> {
  const user = await ensureUser();
  if (!user) return null;
  const { data } = await supabase.from('profiles').select('org_id').eq('id', user.id).single();
  return data?.org_id ?? null;
}

// Convert DB row to TaxScenario
export function rowToScenario(row: any): TaxScenario {
  return {
    id: row.id,
    clientId: row.client_id,
    // Fallback to client_id if root_client_id isn't populated yet (e.g. a row inserted
    // during the brief window between migration apply and DB cache refresh).
    rootClientId: row.root_client_id ?? row.client_id,
    input: row.input as TaxScenarioInput,
    results: normalizeAltFundFields(row.results as any),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    isDraft: row.is_draft ?? false,
    sortOrder: row.sort_order ?? 0,
    userId: row.user_id,
  };
}

export async function loadScenarios(): Promise<TaxScenario[]> {
  const user = await ensureUser();
  if (!user) return [];
  return withAuthRetry(async () => {
    const { data, error } = await supabase
      .from('scenarios')
      .select('*')
      .eq('is_draft', false)
      .eq('user_id', user.id)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(rowToScenario);
  });
}

export async function loadDrafts(): Promise<TaxScenario[]> {
  const user = await ensureUser();
  if (!user) return [];
  return withAuthRetry(async () => {
    const { data, error } = await supabase
      .from('scenarios')
      .select('*')
      .eq('is_draft', true)
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(rowToScenario);
  });
}

// Mutations are wrapped in withAuthRetry: a retry only fires on an auth failure,
// which means the server REJECTED the request (no row written), so re-running is safe.
export async function createScenario(input: TaxScenarioInput, userId: string, clientId?: string): Promise<TaxScenario> {
  return withAuthRetry(async () => {
    const results = await calculateTaxServer(input);
    const orgId = await getUserOrgId();
    let nextSortOrder = 0;
    if (clientId) {
      const { data: maxRow } = await supabase
        .from('scenarios')
        .select('sort_order')
        .eq('client_id', clientId)
        .eq('is_draft', false)
        .order('sort_order', { ascending: false })
        .limit(1)
        .maybeSingle();
      nextSortOrder = (maxRow?.sort_order ?? -1) + 1;
    }
    const row: any = { user_id: userId, name: input.name, input: input as any, results: results as any, is_draft: false, org_id: orgId, sort_order: nextSortOrder };
    if (clientId) row.client_id = clientId;
    const { data, error } = await supabase
      .from('scenarios')
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return rowToScenario(data);
  });
}

export async function saveDraftScenario(input: TaxScenarioInput, userId: string, clientId?: string): Promise<TaxScenario> {
  return withAuthRetry(async () => {
    const partialResults = { totalTaxLiability: 0, effectiveTaxRate: 0, adjustedGrossIncome: 0 };
    const orgId = await getUserOrgId();
    const row: any = { user_id: userId, name: input.name || 'Draft', input: input as any, results: partialResults as any, is_draft: true, org_id: orgId };
    if (clientId) row.client_id = clientId;
    const { data, error } = await supabase
      .from('scenarios')
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return rowToScenario(data);
  });
}

export async function updateDraftScenario(id: string, input: TaxScenarioInput): Promise<TaxScenario> {
  return withAuthRetry(async () => {
    const partialResults = { totalTaxLiability: 0, effectiveTaxRate: 0, adjustedGrossIncome: 0 };
    const { data, error } = await supabase
      .from('scenarios')
      .update({ name: input.name || 'Draft', input: input as any, results: partialResults as any, is_draft: true })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return rowToScenario(data);
  });
}

/** Finalize a draft — compute real results and clear is_draft */
export async function finalizeDraft(id: string, input: TaxScenarioInput): Promise<TaxScenario> {
  return withAuthRetry(async () => {
    const results = await calculateTaxServer(input);
    const { data, error } = await supabase
      .from('scenarios')
      .update({ name: input.name, input: input as any, results: results as any, is_draft: false })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return rowToScenario(data);
  });
}

// Do not include `sort_order` in this update. Baseline position (sort_order=0) is set at
// creation and only mutated by the `reorder_scenarios` RPC; touching it here would let a
// routine edit reshuffle the client group.
export async function updateScenario(id: string, input: TaxScenarioInput): Promise<TaxScenario> {
  return withAuthRetry(async () => {
    const results = await calculateTaxServer(input);
    const { data, error } = await supabase
      .from('scenarios')
      .update({ name: input.name, input: input as any, results: results as any })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return rowToScenario(data);
  });
}

// Pure rename — updates the name column + input.name only. No recalculation
// (the name doesn't affect any math) and results are left untouched, so this
// is safe to call without hitting the calculate-tax edge function.
export async function renameScenario(id: string, input: TaxScenarioInput): Promise<TaxScenario> {
  return withAuthRetry(async () => {
    const { data, error } = await supabase
      .from('scenarios')
      .update({ name: input.name, input: input as any })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return rowToScenario(data);
  });
}

export async function deleteScenario(id: string): Promise<void> {
  return withAuthRetry(async () => {
    const { error } = await supabase.from('scenarios').delete().eq('id', id);
    if (error) throw error;
  });
}

export async function duplicateScenario(scenario: TaxScenario, userId: string): Promise<TaxScenario> {
  return createScenario({ ...scenario.input, name: `${scenario.input.name} (Copy)` }, userId, scenario.clientId);
}

export async function reorderScenarios(scenarioIds: string[], sortOrders: number[]): Promise<void> {
  return withAuthRetry(async () => {
    const { error } = await supabase.rpc('reorder_scenarios', {
      p_scenario_ids: scenarioIds,
      p_sort_orders: sortOrders,
    });
    if (error) throw error;
  });
}

/** Duplicate a client into the caller's own "My Work" under a fresh client_id —
 * every non-draft scenario, or just the baseline when `baselineOnly`. Server-side
 * RPC enforces org membership; case_assignments are intentionally not copied.
 * Returns the new client_id. */
export async function duplicateClient(sourceClientId: string, baselineOnly = false): Promise<string> {
  return withAuthRetry(async () => {
    // `duplicate_client` isn't in the auto-generated Supabase types yet (that file is
    // off-limits per CLAUDE.md). Cast through unknown to satisfy the typed rpc signature.
    const { data, error } = await (supabase.rpc as unknown as (
      fn: 'duplicate_client',
      args: { p_source_client_id: string; p_baseline_only: boolean },
    ) => Promise<{ data: string | null; error: Error | null }>)('duplicate_client', {
      p_source_client_id: sourceClientId,
      p_baseline_only: baselineOnly,
    });
    if (error) throw error;
    if (!data) throw new Error('duplicate_client returned no client id');
    return data;
  });
}
